import React, { useEffect, useState } from "react";
import { Elements, useStripe } from "@stripe/react-stripe-js";
import { call } from "services/axiosService/core";
import getStripe from "./getStripe";
import { useMaterialUIController } from "context";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDButton from "components/MDButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import * as Sentry from "@sentry/browser";
import { useTranslation } from "react-i18next";

function Inner3DSConfirm({ profile }) {
  const stripe = useStripe();
  const { t } = useTranslation();
  const [status, setStatus] = useState("confirming"); // confirming | success | error
  const [message, setMessage] = useState("");

  const finalize = (paymentIntentId, userId) => {
    call(`payments/complete-3ds/${userId}`, "PUT", {
      body: { intent_id: paymentIntentId },
    })
      .then((response) => {
        if (response.status === 200) {
          setStatus("success");
        } else {
          setStatus("error");
          setMessage(t("messages.subscription.threeDsNotFinalized"));
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        setStatus("error");
        setMessage(t("messages.subscription.threeDsUnexpectedError"));
      });
  };

  useEffect(() => {
    if (!stripe) return;

    const params = new URLSearchParams(window.location.search);
    const userId = params.get("uid");

    if (!userId) {
      setStatus("error");
      setMessage(t("messages.subscription.threeDsInvalidLink"));
      return;
    }

    // Case 1: Returning from Stripe redirect (3DS1 older card redirect flow)
    // Stripe appends payment_intent_client_secret + redirect_status to the return_url
    const redirectClientSecret = params.get("payment_intent_client_secret");
    if (redirectClientSecret) {
      stripe
        .retrievePaymentIntent(redirectClientSecret)
        .then(({ paymentIntent, error }) => {
          if (error || !paymentIntent) {
            setStatus("error");
            setMessage(t("messages.subscription.threeDsUnexpectedError"));
            return;
          }
          if (paymentIntent.status === "succeeded") {
            finalize(paymentIntent.id, userId);
          } else {
            setStatus("error");
            setMessage(t("messages.subscription.threeDsNotConfirmed"));
          }
        })
        .catch((err) => {
          Sentry.captureException(err);
          setStatus("error");
          setMessage(t("messages.subscription.threeDsUnexpectedError"));
        });
      return;
    }

    // Case 2: Fresh from email link.
    // Stripe clears the payment method from the PI when authentication_required is raised
    // off-session (PI ends up in requires_payment_method, not requires_action).
    // Re-confirm with the original payment method so Stripe triggers the 3DS challenge.
    const clientSecret = params.get("pi_cs");
    const pmId = params.get("pm");
    if (!clientSecret || !pmId) {
      setStatus("error");
      setMessage(t("messages.subscription.threeDsInvalidLink"));
      return;
    }

    stripe
      .confirmCardPayment(clientSecret, { payment_method: pmId })
      .then(({ paymentIntent, error }) => {
        if (error) {
          setStatus("error");
          setMessage(error.message || t("messages.subscription.threeDsUnexpectedError"));
          return;
        }
        if (paymentIntent?.status === "succeeded") {
          finalize(paymentIntent.id, userId);
        } else {
          setStatus("error");
          setMessage(t("messages.subscription.threeDsNotConfirmed"));
        }
      })
      .catch((err) => {
        Sentry.captureException(err);
        setStatus("error");
        setMessage(t("messages.subscription.threeDsUnexpectedError"));
      });
  }, [stripe]);

  return (
    <Dialog open>
      {status === "confirming" && (
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            minHeight: "200px",
            minWidth: "300px",
            gap: 2,
            padding: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="h6" textAlign="center">
            {t("loaderText.completingAuthentication")}
          </Typography>
        </DialogContent>
      )}

      {status === "success" && (
        <>
          <DialogContent sx={{ minWidth: "300px", padding: 4 }}>
            <Typography variant="h5" textAlign="center" mb={1}>
              {t("headings.paymentVerified")}
            </Typography>
            <Typography variant="body1" textAlign="center" color="text.secondary">
              {t("messages.subscription.threeDsSuccess")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <MDButton
              onClick={() => (window.location.href = "/profile")}
              style={{ color: "white", background: "#10B0D5" }}
            >
              {t("buttonTitles.goToProfile")}
            </MDButton>
          </DialogActions>
        </>
      )}

      {status === "error" && (
        <>
          <DialogContent sx={{ minWidth: "300px", padding: 4 }}>
            <Typography variant="h5" textAlign="center" mb={1}>
              {t("headings.authenticationFailed")}
            </Typography>
            <Typography variant="body1" textAlign="center" color="error">
              {message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <MDButton
              onClick={() => (window.location.href = "/profile")}
              style={{ color: "white", background: "#10B0D5" }}
            >
              {t("buttonTitles.goToProfile")}
            </MDButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

export default function ThreeDSConfirmPage() {
  const { t } = useTranslation();
  const [controller] = useMaterialUIController();
  const { profile } = controller;
  const [stripePromise, setStripePromise] = useState(null);

  const payerId = new URLSearchParams(window.location.search).get("payer");
  const isUnauthorized = payerId && profile.id !== payerId;

  useEffect(() => {
    if (isUnauthorized) return;
    call(`payments/config/${profile.id}`, "GET", {})
      .then((response) => {
        if (response.status === 200 && response.data.publish_key) {
          setStripePromise(getStripe(response.data.publish_key));
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
      });
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar title={t("pageTitles.completePayment")} />
      {isUnauthorized ? (
        <Dialog open>
          <DialogContent sx={{ minWidth: "300px", padding: 4 }}>
            <Typography variant="h5" textAlign="center" mb={1}>
              {t("headings.authenticationFailed")}
            </Typography>
            <Typography variant="body1" textAlign="center" color="error">
              {t("messages.subscription.threeDsUnauthorized")}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
            <MDButton
              onClick={() => (window.location.href = "/profile")}
              style={{ color: "white", background: "#10B0D5" }}
            >
              {t("buttonTitles.goToProfile")}
            </MDButton>
          </DialogActions>
        </Dialog>
      ) : stripePromise ? (
        <Elements stripe={stripePromise}>
          <Inner3DSConfirm profile={profile} />
        </Elements>
      ) : (
        <Dialog open>
          <DialogContent
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "200px",
              minWidth: "300px",
              gap: 2,
              padding: 4,
            }}
          >
            <CircularProgress />
            <Typography variant="h6" textAlign="center">
              {t("loaderText.loadingPaymentDetails")}
            </Typography>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
}
